import React, { useCallback, useRef, useState } from 'react';
import { UploadCloud, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useS3Upload from '../../hooks/useS3Upload';

/**
 * Drop-zone + queue UI for uploading media to a tenant's marketing site.
 *
 * Props:
 *   folder         — required. The logical bucket (hero/sections/blog/...).
 *   accept         — HTML accept attribute (e.g. "image/*").
 *   onAssetReady   — callback(asset) when each upload confirms.
 *   compact        — bool; if true shows a slim single-row layout for dialogs.
 */
const statusMeta = {
  queued:     { label: 'في الانتظار', color: 'bg-gray-100 text-gray-600' },
  uploading:  { label: 'جاري الرفع',  color: 'bg-blue-100 text-blue-700' },
  confirming: { label: 'جاري التأكيد', color: 'bg-indigo-100 text-indigo-700' },
  done:       { label: 'تم',           color: 'bg-emerald-100 text-emerald-700' },
  error:      { label: 'فشل',          color: 'bg-rose-100 text-rose-700' },
  cancelled:  { label: 'ملغي',         color: 'bg-amber-100 text-amber-700' },
};

function prettySize(bytes) {
  if (!bytes) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

export default function MediaUploader({
  folder,
  accept = 'image/jpeg,image/png,image/webp,image/avif',
  onAssetReady,
  compact = false,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const { uploadFiles, queue, cancel, clearFinished, isUploading } = useS3Upload({
    folder,
    onAssetReady,
  });

  const onPickFiles = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onFilesSelected = useCallback(async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    await uploadFiles(list);
    // Give user a beat to see success, then trim
    setTimeout(clearFinished, 2500);
  }, [uploadFiles, clearFinished]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    onFilesSelected(e.dataTransfer.files);
  };

  return (
    <div dir="rtl">
      <div
        onClick={onPickFiles}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={[
          'rounded-2xl border-2 border-dashed transition-all cursor-pointer text-center',
          compact ? 'p-4' : 'p-8',
          dragging
            ? 'border-blue-400 bg-blue-50/50'
            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50/80',
        ].join(' ')}
      >
        <div className={`w-${compact ? '10' : '14'} h-${compact ? '10' : '14'} mx-auto mb-2 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm`}>
          <UploadCloud className={`${compact ? 'w-5 h-5' : 'w-7 h-7'} text-white`} />
        </div>
        <p className="text-sm font-semibold text-gray-800">
          {dragging ? 'أسقط الملفات هنا' : 'اسحب الصور هنا أو اضغط لاختيارها'}
        </p>
        {!compact && (
          <p className="text-xs text-gray-500 mt-1">JPG / PNG / WebP / AVIF — حتى 5 ميجا</p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => onFilesSelected(e.target.files)}
        />
      </div>

      {/* Queue list */}
      <AnimatePresence>
        {queue.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-2"
          >
            {queue.map((item) => {
              const meta = statusMeta[item.status] || statusMeta.queued;
              return (
                <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white">
                  <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                    {item.status === 'done' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                    {item.status === 'error' && <AlertCircle className="w-5 h-5 text-rose-600" />}
                    {(item.status === 'uploading' || item.status === 'confirming') && (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    )}
                    {(item.status === 'queued' || item.status === 'cancelled') && (
                      <UploadCloud className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="truncate font-medium text-gray-800">{item.name}</span>
                      <span className="text-xs text-gray-400 shrink-0">{prettySize(item.size)}</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          item.status === 'error' ? 'bg-rose-500' :
                          item.status === 'done' ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                    {item.error && (
                      <p className="mt-1 text-xs text-rose-600 truncate">{item.error}</p>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                    {meta.label}
                  </span>
                  {['queued', 'uploading', 'confirming'].includes(item.status) && (
                    <button
                      onClick={() => cancel(item.id)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                      title="إلغاء"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
            {!isUploading && queue.some((q) => ['done', 'cancelled', 'error'].includes(q.status)) && (
              <button
                onClick={clearFinished}
                className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
              >
                مسح المكتملة
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
